import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, StatusChip, UserNameCell } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { ASSESSMENT_TYPES } from '@/modules/shared/hr-form-engine';
import { formatDate } from '@/modules/performance/hr-review-core';
import { PeerRequest } from '../../types/peer-request.types';

interface CreatePeerRequestsTableColumnsProps {
  t: (key: string) => string;
  departmentNames: Map<string, string>;
  designationNames: Map<string, string>;
  /** The employee's email by IAM user id, shown under their name. */
  emailOf?: (userId: string) => string | undefined;
  onSelectPeers: (request: PeerRequest) => void;
  onForward: (request: PeerRequest) => void;
}

export const createPeerRequestsTableColumns = ({
  t,
  departmentNames,
  designationNames,
  emailOf,
  onSelectPeers,
  onForward,
}: CreatePeerRequestsTableColumnsProps): ColumnDef<PeerRequest>[] => [
  {
    id: 'Name',
    // Filtered by the toolbar's name search.
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.review.Name,
    cell: ({ row }) => (
      <UserNameCell
        name={row.original.review.Name}
        email={emailOf?.(row.original.review.ReviewForId)}
      />
    ),
  },
  {
    id: 'Status',
    // The manager's own selection status, worked out from the review; not a field of its own.
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.status,
    cell: ({ row }) => (
      <StatusChip status={row.original.status}>{t(row.original.status.toUpperCase())}</StatusChip>
    ),
  },
  {
    id: 'Department',
    // Looked up from the review's department id; filtered by the toolbar.
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => departmentNames.get(row.review.DepartmentId ?? '') ?? '-',
  },
  {
    id: 'Designation',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESIGNATION')} />,
    accessorFn: (row) => designationNames.get(row.review.DesignationId ?? '') ?? '-',
  },
  {
    id: 'AssessmentType',
    meta: {
      filter: {
        type: 'options',
        options: ASSESSMENT_TYPES.map((type) => ({ value: type.key, label: t(type.title) })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('ASSESSMENT_TYPE')} />,
    accessorFn: (row) =>
      t(ASSESSMENT_TYPES.find((type) => type.key === row.review.AssessmentType)?.title ?? '-'),
  },
  {
    id: 'RequestDate',
    // Read from ReviewDataJson; not a field the server can sort or filter on.
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REQUEST_DATE')} />,
    accessorFn: (row) => formatDate(row.peerData.RequestDate ?? row.review.CreatedDate),
  },
  {
    id: 'Deadline',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEADLINE')} />,
    accessorFn: (row) => formatDate(row.deadline),
  },
  {
    id: 'actions',
    // Card: top right, as the old portal's card header.
    meta: { mobile: 'badge' as const },
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const isTransferred = row.original.status === 'Transferred';
      return (
        <div className="flex gap-2" onClick={(event) => event.stopPropagation()}>
          <Button
            variant="outline"
            size="sm"
            disabled={isTransferred}
            onClick={() => onSelectPeers(row.original)}
          >
            {t('SELECT_PEERS')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isTransferred}
            onClick={() => onForward(row.original)}
          >
            {t('FORWARD')}
          </Button>
        </div>
      );
    },
  },
];
